import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import "../pd-popup.js";
import "../pd-assistant-input-bar.js";
import { LitElement, css, html, nothing } from "lit";
import { property, query } from "lit/decorators.js";
import { pdIcons } from "@progressive-development/pd-icon";
import "@progressive-development/pd-icon/pd-icon";
//#region src/pd-assistant-dialog/PdAssistantDialog.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*/
/**
* Assistant-style dialog shell with header, scrollable content area, and input bar footer.
* Wraps pd-popup via composition. Reusable for AI chat, image search, etc.
*
* @tagname pd-assistant-dialog
* @summary Assistant dialog with header, content slot, and input bar.
*
* @event pd-assistant-dialog-close - Fired when dialog is closed.
* @event pd-assistant-submit - Bubbles from the input bar on submit.
*
* @slot - Default slot for main content (results, messages, etc.).
*
* @cssprop --pd-assistant-dialog-width - Dialog width. Default: `min(520px, 90vw)`.
* @cssprop --pd-assistant-dialog-content-min-height - Min content height. Default: `120px`.
* @cssprop --pd-assistant-dialog-content-max-height - Max content height. Default: `50vh`.
*/
var PdAssistantDialog = class extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.open = false;
		this.dialogTitle = "";
		this.dialogIcon = "";
		this.inputPlaceholder = "Suchbegriff eingeben...";
		this.inputButtonText = "Suchen";
		this.inputDisabled = false;
		this.hideInput = false;
	}
	static {
		this.styles = css`
    :host {
      display: contents;
    }

    pd-popup {
      --pd-popup-width: var(--pd-assistant-dialog-width, min(520px, 90vw));
      --pd-popup-max-width: var(--pd-assistant-dialog-width, min(520px, 90vw));
      --pd-popup-modal-padding: 0;
      --pd-popup-modal-padding-bottom: 0;
      --pd-popup-modal-padding-top: 10vh;
      --pd-popup-border-radius: 12px;
      --pd-popup-modal-slot-max-width: 100%;
      --pd-popup-modal-slot-margin: 0;
    }

    .dialog-header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 16px 20px;
      border-bottom: 1px solid var(--pd-default-light-col, #e0e0e0);
    }

    .dialog-header h3 {
      margin: 0;
      font-size: 1rem;
      font-weight: 600;
      flex: 1;
    }

    .dialog-header .header-icon {
      --pd-icon-size: 1.25rem;
      color: var(--pd-default-col, #0066cc);
    }

    .close-icon {
      cursor: pointer;
      --pd-icon-size: 1.25rem;
    }

    .close-icon:focus {
      outline: 2px solid var(--pd-default-col);
      outline-offset: 2px;
      border-radius: var(--pd-radius-sm, 2px);
    }

    .dialog-content {
      flex: 1;
      overflow-y: auto;
      min-height: var(--pd-assistant-dialog-content-min-height, 120px);
      max-height: var(--pd-assistant-dialog-content-max-height, 50vh);
      padding: 16px 20px;
    }
  `;
	}
	updated(changed) {
		if (changed.has("open")) {
			if (this.open) this._popup?.showPopup();
			else this._popup?.hidePopup();
		}
	}
	render() {
		return html`
      <pd-popup
        closeByEscape
        closeByBackdrop
        blockScroll
        hideCloseIcon
        popupLabel="${this.dialogTitle}"
        @popup-close="${this._onPopupClose}"
      >
        <div slot="content">
          <div class="dialog-header">
            ${this.dialogIcon ? html`<pd-icon
                    class="header-icon"
                    .icon="${this.dialogIcon}"
                  ></pd-icon>` : nothing}
            <h3>${this.dialogTitle}</h3>
            <pd-icon
              class="close-icon"
              .icon="${pdIcons.ICON_CLOSE}"
              tabindex="0"
              role="button"
              aria-label="Schließen"
              @click="${this.hide}"
              @keydown="${this._onCloseKeyDown}"
            ></pd-icon>
          </div>

          <div class="dialog-content">
            <slot></slot>
          </div>

          ${this.hideInput ? nothing : html`
                  <pd-assistant-input-bar
                    placeholder="${this.inputPlaceholder}"
                    buttonText="${this.inputButtonText}"
                    ?disabled="${this.inputDisabled}"
                  ></pd-assistant-input-bar>
                `}
        </div>
      </pd-popup>
    `;
	}
	/** Open the dialog. */
	show() {
		this.open = true;
	}
	/** Close the dialog. */
	hide() {
		this.open = false;
	}
	/** Clear the input bar value. */
	clearInput() {
		this.renderRoot.querySelector("pd-assistant-input-bar")?.clear();
	}
	_onPopupClose() {
		this.open = false;
		this.dispatchEvent(new CustomEvent("pd-assistant-dialog-close", {
			bubbles: true,
			composed: true
		}));
	}
	_onCloseKeyDown(e) {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			this.hide();
		}
	}
};
__decorate([property({
	type: Boolean,
	reflect: true
})], PdAssistantDialog.prototype, "open", void 0);
__decorate([property({ type: String })], PdAssistantDialog.prototype, "dialogTitle", void 0);
__decorate([property({ type: String })], PdAssistantDialog.prototype, "dialogIcon", void 0);
__decorate([property({ type: String })], PdAssistantDialog.prototype, "inputPlaceholder", void 0);
__decorate([property({ type: String })], PdAssistantDialog.prototype, "inputButtonText", void 0);
__decorate([property({ type: Boolean })], PdAssistantDialog.prototype, "inputDisabled", void 0);
__decorate([property({ type: Boolean })], PdAssistantDialog.prototype, "hideInput", void 0);
__decorate([query("pd-popup")], PdAssistantDialog.prototype, "_popup", void 0);
//#endregion
export { PdAssistantDialog };
